import { tv } from "tailwind-variants";

import { analytics } from "@/config/analytics";
import { useCareplanOrder } from "@/domains/careplan/hooks/use-careplan-order";
import { useIsTopFractionInView } from "@/hooks/use-is-top-fraction-in-view";
import { useCartStore } from "@/stores";
import { Button, icons, Skeleton } from "@/ui";

const REVEAL_FRACTION = 0.25;

const stickyCheckoutBarVariants = tv({
  base: [
    "sticky inset-x-0 bottom-0 z-10 border-t border-line bg-surface px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-bar",
    "transition-transform duration-300 ease-out motion-reduce:transition-none md:hidden",
  ],
  variants: {
    isHidden: {
      true: "translate-y-full",
      false: "translate-y-0",
    },
  },
});

type StickyCheckoutBarProps = {
  isEmpty?: boolean;
  revealTarget: HTMLElement | null;
};

export const StickyCheckoutBar = ({ isEmpty = false, revealTarget }: StickyCheckoutBarProps) => {
  const { isLoading, order } = useCareplanOrder();
  const openCart = useCartStore((state) => {
    return state.openCart;
  });
  const showCart = useCartStore((state) => {
    return state.showCart;
  });
  const isTargetInView = useIsTopFractionInView(revealTarget, REVEAL_FRACTION);

  const isHidden = showCart || !isTargetInView;

  const handleClick = () => {
    analytics.track("careplan_sticky_checkout_clicked");
    openCart();
  };

  const renderAmount = () => {
    if (isLoading) {
      return (
        <>
          <Skeleton className="h-5 w-16" />
          <span className="sr-only">Loading your monthly total</span>
        </>
      );
    }

    if (isEmpty) {
      return <span className="text-base whitespace-nowrap">Nothing in your order</span>;
    }

    if (!order || order.isEmpty) {
      return null;
    }

    return <span className="whitespace-nowrap">{`$${order.totalAsString}/mo`}</span>;
  };

  return (
    <div
      aria-hidden={isHidden || undefined}
      className={stickyCheckoutBarVariants({ isHidden })}
      inert={isHidden}
    >
      <Button
        className="w-full justify-between px-6"
        disabled={isEmpty}
        onClick={handleClick}
        size="lg"
      >
        <span className="flex items-center gap-2 whitespace-nowrap">
          <span>View Cart</span>
          <icons.ArrowRight aria-hidden="true" className="size-5" />
        </span>
        {renderAmount()}
      </Button>
    </div>
  );
};
